import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout, { adminFetch, SectionTitle, AdminCard, useAdmin } from '../AdminLayout';

// ── Constantes ────────────────────────────────────────────────────────────────

const ORGANISMES = [
  'CAF', 'CNAV', 'MSA', 'CARSAT', 'Mairie', 'Conseil Départemental',
  'Région', 'France Travail', 'Préfecture', 'CCAS', 'Département', 'autre',
];

const PORTEES = ['nationale', 'régionale', 'départementale', 'communale'];
const UNITES  = ['mois', 'an', 'jour', 'unique'];
const STATUTS = ['actif', 'à vérifier', 'supprimé'];

const FORM_INITIAL = {
  id: '',
  nom: '',
  organisme: 'CAF',
  description: '',
  conditionAge: 65,
  conditionsRessources: '',
  montant: { min: '', max: '', unite: 'mois' },
  documentsRequis: '',
  delaiTraitement: '1 mois',
  urlOfficielle: 'https://',
  couverture: { portee: 'nationale', departements: 'tous' },
  priorite: 2,
  statut: 'actif',
  note: '',
  // P3 — vulgarisation pour fiche agent (multi-lignes, 1 item par ligne)
  exemplesConcrets: '',
  piegesCourants: '',
  reason: '',
};

// ── Composant principal ────────────────────────────────────────────────────────

export default function AideEditPage() {
  return (
    <AdminLayout title="Catalogue des aides">
      <AideEditContent />
    </AdminLayout>
  );
}

// ── Contenu ────────────────────────────────────────────────────────────────────

function AideEditContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const { dark, border } = useAdmin();
  const textColor  = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  const [form, setForm]       = useState(FORM_INITIAL);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(false);

  // ── Chargement de l'aide à éditer ──────────────────────────────────────────

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await adminFetch(`/api/admin/aides/catalogue/${encodeURIComponent(id)}`);
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || `Erreur serveur (${res.status})`);

        const aide = body.aide || body;
        if (!cancelled) {
          setForm({
            id: aide.id || id,
            nom: aide.nom || '',
            organisme: aide.organisme || 'CAF',
            description: aide.description || '',
            conditionAge: aide.conditions_age ?? aide.conditionAge ?? 0,
            conditionsRessources: aide.conditions_ressources ?? aide.conditionsRessources ?? '',
            montant: {
              min: aide.montant_min ?? aide.montant?.min ?? '',
              max: aide.montant_max ?? aide.montant?.max ?? '',
              unite: aide.unite ?? aide.montant?.unite ?? 'mois',
            },
            documentsRequis: (aide.documents_requis ?? aide.documentsRequis ?? []).join('\n'),
            delaiTraitement: aide.delai_traitement ?? aide.delaiTraitement ?? '',
            urlOfficielle: aide.url_officielle ?? aide.urlOfficielle ?? 'https://',
            couverture: {
              portee: aide.couverture_geographique ?? aide.couverture?.portee ?? 'nationale',
              departements: (aide.departements_concernes ?? aide.couverture?.departements ?? ['tous']).join(', '),
            },
            priorite: aide.priorite ?? 2,
            statut: aide.statut ?? 'actif',
            note: aide.note ?? '',
            exemplesConcrets: (aide.exemples_concrets ?? aide.exemplesConcrets ?? []).join('\n'),
            piegesCourants: (aide.pieges_courants ?? aide.piegesCourants ?? []).join('\n'),
            reason: '',
          });
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Erreur de chargement de l\'aide.');
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [id, isNew]);

  // ── Mise à jour d'un champ ──────────────────────────────────────────────────

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setMontant = (field, value) => setForm(prev => ({
    ...prev,
    montant: { ...prev.montant, [field]: value },
  }));
  const setCouverture = (field, value) => setForm(prev => ({
    ...prev,
    couverture: { ...prev.couverture, [field]: value },
  }));

  // ── Soumission ──────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!form.reason || form.reason.trim().length < 3) {
      setError('La raison du changement est obligatoire (minimum 3 caractères).');
      return;
    }

    setSaving(true);

    const documentsRequisArray = form.documentsRequis
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

    const exemplesArray = (form.exemplesConcrets || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

    const piegesArray = (form.piegesCourants || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);

    const departementsArray = form.couverture.departements
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    try {
      if (isNew) {
        const payload = {
          id: form.id,
          nom: form.nom,
          organisme: form.organisme,
          description: form.description,
          conditionAge: parseInt(form.conditionAge, 10) || 0,
          conditionsRessources: form.conditionsRessources,
          montant: {
            min: form.montant.min !== '' ? parseFloat(form.montant.min) : null,
            max: form.montant.max !== '' ? parseFloat(form.montant.max) : null,
            unite: form.montant.unite,
          },
          documentsRequis: documentsRequisArray,
          delaiTraitement: form.delaiTraitement,
          urlOfficielle: form.urlOfficielle,
          couverture: {
            portee: form.couverture.portee,
            departements: departementsArray.length ? departementsArray : ['tous'],
          },
          priorite: parseInt(form.priorite, 10) || 2,
          statut: form.statut,
          note: form.note || null,
          exemplesConcrets: exemplesArray,
          piegesCourants: piegesArray,
          reason: form.reason.trim(),
        };

        const res = await adminFetch('/api/admin/aides/catalogue', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || `Erreur serveur (${res.status})`);
      } else {
        const changes = {
          nom: form.nom,
          organisme: form.organisme,
          description: form.description,
          conditionAge: parseInt(form.conditionAge, 10) || 0,
          conditionsRessources: form.conditionsRessources,
          montant: {
            min: form.montant.min !== '' ? parseFloat(form.montant.min) : null,
            max: form.montant.max !== '' ? parseFloat(form.montant.max) : null,
            unite: form.montant.unite,
          },
          documentsRequis: documentsRequisArray,
          delaiTraitement: form.delaiTraitement,
          urlOfficielle: form.urlOfficielle,
          couverture: {
            portee: form.couverture.portee,
            departements: departementsArray.length ? departementsArray : ['tous'],
          },
          priorite: parseInt(form.priorite, 10) || 2,
          statut: form.statut,
          note: form.note || null,
          exemplesConcrets: exemplesArray,
          piegesCourants: piegesArray,
        };

        const res = await adminFetch(`/api/admin/aides/catalogue/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: JSON.stringify({ changes, reason: form.reason.trim() }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || `Erreur serveur (${res.status})`);
      }

      setSuccess(true);
      setTimeout(() => navigate('/admin/aides/catalogue'), 1500);
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  // ── Suppression ─────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!window.confirm(`Confirmer la suppression de "${form.nom}" (soft-delete) ?`)) return;
    const reason = window.prompt('Raison de la suppression :');
    if (!reason || reason.trim().length < 3) {
      alert('La raison est obligatoire (minimum 3 caractères).');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await adminFetch(`/api/admin/aides/catalogue/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `Erreur serveur (${res.status})`);
      navigate('/admin/aides/catalogue');
    } catch (err) {
      setError(err.message || 'Erreur lors de la suppression.');
      setSaving(false);
    }
  };

  // ── Styles helpers ──────────────────────────────────────────────────────────

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: `1px solid ${border}`,
    background: dark ? '#0f0f23' : '#F5F3F0',
    color: textColor,
    fontSize: 15,
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 500,
    color: textColor,
  };

  const rowStyle = {
    marginBottom: 20,
  };

  const fieldsetStyle = {
    border: `1px solid ${border}`,
    borderRadius: 10,
    padding: '16px 20px',
    marginBottom: 20,
  };

  const legendStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: mutedColor,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '0 8px',
  };

  // ── Chargement ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div>
        <SectionTitle>Chargement de l'aide…</SectionTitle>
        <AdminCard>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                style={{
                  height: 44, borderRadius: 8,
                  background: dark ? '#1e1e3a' : '#F5F3F0',
                  opacity: 0.5 + i * 0.08,
                }}
              />
            ))}
          </div>
        </AdminCard>
      </div>
    );
  }

  // ── Rendu ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* En-tête */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexWrap: 'wrap', gap: 16, marginBottom: 24,
      }}>
        <SectionTitle>
          {isNew ? 'Nouvelle aide' : `Éditer : ${form.nom || id}`}
        </SectionTitle>
        <Link
          to="/admin/aides/catalogue"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '10px 18px', borderRadius: 10,
            border: `1px solid ${border}`,
            background: 'transparent', color: dark ? '#e0e0e0' : '#2D2D2D',
            fontSize: 14, fontWeight: 500, textDecoration: 'none',
          }}
        >
          ← Retour à la liste
        </Link>
      </div>

      {/* Alertes */}
      {error && (
        <div
          role="alert"
          style={{
            background: '#FEE2E2', border: '1px solid rgba(192,57,43,0.4)',
            borderRadius: 12, padding: '14px 20px', marginBottom: 20,
            color: '#991B1B', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <span style={{ fontSize: 18 }}>⚠️</span>
          {error}
        </div>
      )}
      {success && (
        <div
          role="status"
          style={{
            background: '#D1FAE5', border: '1px solid rgba(61,139,94,0.4)',
            borderRadius: 12, padding: '14px 20px', marginBottom: 20,
            color: '#065F46', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10,
          }}
        >
          <span style={{ fontSize: 18 }}>✅</span>
          Sauvegardé ! Redirection en cours…
        </div>
      )}

      <AdminCard>
        <form onSubmit={handleSubmit} noValidate>

          {/* ── Section Identité ── */}
          {isNew && (
            <div style={rowStyle}>
              <label htmlFor="aide-id" style={labelStyle}>
                Identifiant *
                <span style={{ fontWeight: 400, color: mutedColor, marginLeft: 8, fontSize: 13 }}>
                  (format : nat-001, reg-idf-001, dep-001, com-001)
                </span>
              </label>
              <input
                id="aide-id"
                type="text"
                value={form.id}
                onChange={e => set('id', e.target.value)}
                style={inputStyle}
                placeholder="nat-042"
                pattern="^(nat|reg-[a-z]{2,4}|dep|com)-\d{3,}$"
                title="Format attendu : nat-NNN, reg-XX-NNN, dep-NNN ou com-NNN"
                required
                autoFocus
              />
            </div>
          )}

          <div style={rowStyle}>
            <label htmlFor="aide-nom" style={labelStyle}>Nom de l'aide *</label>
            <input
              id="aide-nom"
              type="text"
              value={form.nom}
              onChange={e => set('nom', e.target.value)}
              style={inputStyle}
              maxLength={200}
              required
            />
          </div>

          <div style={rowStyle}>
            <label htmlFor="aide-organisme" style={labelStyle}>Organisme *</label>
            <select
              id="aide-organisme"
              value={form.organisme}
              onChange={e => set('organisme', e.target.value)}
              style={inputStyle}
              required
            >
              {ORGANISMES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div style={rowStyle}>
            <label htmlFor="aide-description" style={labelStyle}>Description *</label>
            <textarea
              id="aide-description"
              rows={5}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              required
            />
          </div>

          {/* ── Section Conditions ── */}
          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>Conditions d'éligibilité</legend>

            <div style={rowStyle}>
              <label htmlFor="aide-age" style={labelStyle}>
                Âge minimum
                <span style={{ fontWeight: 400, color: mutedColor, marginLeft: 8, fontSize: 13 }}>
                  (0 = aucune condition d'âge)
                </span>
              </label>
              <input
                id="aide-age"
                type="number"
                min={0}
                max={120}
                value={form.conditionAge}
                onChange={e => set('conditionAge', e.target.value)}
                style={{ ...inputStyle, maxWidth: 160 }}
              />
            </div>

            <div style={{ ...rowStyle, marginBottom: 0 }}>
              <label htmlFor="aide-ressources" style={labelStyle}>Conditions de ressources</label>
              <textarea
                id="aide-ressources"
                rows={3}
                value={form.conditionsRessources}
                onChange={e => set('conditionsRessources', e.target.value)}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Ex : Plafond ASPA, ressources inférieures à 1 000 €/mois…"
              />
            </div>
          </fieldset>

          {/* ── Section Montant ── */}
          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>Montant de l'aide</legend>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label htmlFor="aide-montant-min" style={labelStyle}>Minimum (€)</label>
                <input
                  id="aide-montant-min"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.montant.min}
                  onChange={e => setMontant('min', e.target.value)}
                  style={inputStyle}
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="aide-montant-max" style={labelStyle}>Maximum (€)</label>
                <input
                  id="aide-montant-max"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.montant.max}
                  onChange={e => setMontant('max', e.target.value)}
                  style={inputStyle}
                  placeholder="0"
                />
              </div>
              <div>
                <label htmlFor="aide-unite" style={labelStyle}>Unité</label>
                <select
                  id="aide-unite"
                  value={form.montant.unite}
                  onChange={e => setMontant('unite', e.target.value)}
                  style={inputStyle}
                >
                  {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </fieldset>

          {/* ── Section Documents & délai ── */}
          <div style={rowStyle}>
            <label htmlFor="aide-docs" style={labelStyle}>
              Documents requis
              <span style={{ fontWeight: 400, color: mutedColor, marginLeft: 8, fontSize: 13 }}>
                (un document par ligne)
              </span>
            </label>
            <textarea
              id="aide-docs"
              rows={6}
              value={form.documentsRequis}
              onChange={e => set('documentsRequis', e.target.value)}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }}
              placeholder={`Carte d'identité\nJustificatif de domicile\nDernier avis d'imposition`}
            />
          </div>

          <div style={rowStyle}>
            <label htmlFor="aide-delai" style={labelStyle}>Délai de traitement</label>
            <input
              id="aide-delai"
              type="text"
              value={form.delaiTraitement}
              onChange={e => set('delaiTraitement', e.target.value)}
              style={{ ...inputStyle, maxWidth: 320 }}
              placeholder="Ex : 4 à 8 semaines"
            />
          </div>

          <div style={rowStyle}>
            <label htmlFor="aide-url" style={labelStyle}>URL officielle *</label>
            <input
              id="aide-url"
              type="url"
              value={form.urlOfficielle}
              onChange={e => set('urlOfficielle', e.target.value)}
              style={inputStyle}
              required
            />
          </div>

          {/* ── Section Couverture géographique ── */}
          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>Couverture géographique</legend>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
              <div>
                <label htmlFor="aide-portee" style={labelStyle}>Portée</label>
                <select
                  id="aide-portee"
                  value={form.couverture.portee}
                  onChange={e => setCouverture('portee', e.target.value)}
                  style={inputStyle}
                >
                  {PORTEES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="aide-departements" style={labelStyle}>
                  Départements
                  <span style={{ fontWeight: 400, color: mutedColor, marginLeft: 8, fontSize: 13 }}>
                    (codes séparés par virgule, ou "tous")
                  </span>
                </label>
                <input
                  id="aide-departements"
                  type="text"
                  value={form.couverture.departements}
                  onChange={e => setCouverture('departements', e.target.value)}
                  style={inputStyle}
                  placeholder="tous, ou : 75, 92, 93"
                />
              </div>
            </div>
          </fieldset>

          {/* ── Section Paramètres admin ── */}
          <fieldset style={fieldsetStyle}>
            <legend style={legendStyle}>Paramètres administratifs</legend>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label htmlFor="aide-priorite" style={labelStyle}>Priorité</label>
                <select
                  id="aide-priorite"
                  value={form.priorite}
                  onChange={e => set('priorite', e.target.value)}
                  style={inputStyle}
                >
                  <option value={1}>1 — Haute</option>
                  <option value={2}>2 — Moyenne</option>
                  <option value={3}>3 — Basse</option>
                </select>
              </div>
              <div>
                <label htmlFor="aide-statut" style={labelStyle}>Statut</label>
                <select
                  id="aide-statut"
                  value={form.statut}
                  onChange={e => set('statut', e.target.value)}
                  style={inputStyle}
                >
                  {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label htmlFor="aide-note" style={labelStyle}>Note interne</label>
              <textarea
                id="aide-note"
                rows={2}
                value={form.note}
                onChange={e => set('note', e.target.value)}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Observations internes, points à vérifier…"
              />
            </div>

            {/* P3 — vulgarisation pour fiche agent */}
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="aide-exemples" style={labelStyle}>
                💡 Exemples concrets (1 par ligne)
              </label>
              <textarea
                id="aide-exemples"
                rows={3}
                value={form.exemplesConcrets}
                onChange={e => set('exemplesConcrets', e.target.value)}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Ex: Pour un devis de 5000€, l'aide couvre 70% = 3500€&#10;Ex: Pour un mono-parental avec 2 enfants, plafond ressources 24000€/an"
              />
            </div>

            <div style={{ marginBottom: 0 }}>
              <label htmlFor="aide-pieges" style={labelStyle}>
                ⚠️ Pièges à éviter (1 par ligne)
              </label>
              <textarea
                id="aide-pieges"
                rows={3}
                value={form.piegesCourants}
                onChange={e => set('piegesCourants', e.target.value)}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Ex: Ne pas commencer les travaux avant l'accord ANAH&#10;Ex: Attention au délai de 6 mois après réception du devis"
              />
            </div>
          </fieldset>

          {/* ── Raison du changement (audit trail) ── */}
          <div style={{
            ...rowStyle,
            borderTop: `2px solid ${border}`,
            paddingTop: 20,
            marginTop: 8,
          }}>
            <label htmlFor="aide-reason" style={{ ...labelStyle, color: '#D4724A' }}>
              Raison du changement *
              <span style={{ fontWeight: 400, color: mutedColor, marginLeft: 8, fontSize: 13 }}>
                (obligatoire — tracé dans l'audit trail)
              </span>
            </label>
            <input
              id="aide-reason"
              type="text"
              value={form.reason}
              onChange={e => set('reason', e.target.value)}
              style={{
                ...inputStyle,
                borderColor: '#D4724A',
                outline: 'none',
              }}
              minLength={3}
              required
              placeholder="Ex : Correction du montant suite au décret du 01/01/2026"
            />
          </div>

          {/* ── Actions ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
            flexWrap: 'wrap', paddingTop: 8,
          }}>
            <button
              type="submit"
              disabled={saving || success}
              style={{
                padding: '12px 28px', borderRadius: 10, border: 'none',
                background: saving || success ? mutedColor : '#D4724A',
                color: '#fff', fontSize: 15, fontWeight: 600,
                cursor: saving || success ? 'not-allowed' : 'pointer',
                opacity: saving || success ? 0.7 : 1,
              }}
            >
              {saving ? 'Sauvegarde…' : (isNew ? 'Créer l\'aide' : 'Mettre à jour')}
            </button>

            {!isNew && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                style={{
                  padding: '12px 20px', borderRadius: 10,
                  border: '1px solid rgba(192,57,43,0.5)',
                  background: 'transparent', color: '#C0392B',
                  fontSize: 14, fontWeight: 500,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                Supprimer
              </button>
            )}

            <Link
              to="/admin/aides/catalogue"
              style={{
                padding: '12px 20px', borderRadius: 10,
                border: `1px solid ${border}`,
                color: dark ? '#e0e0e0' : '#2D2D2D',
                fontSize: 14, fontWeight: 500, textDecoration: 'none',
              }}
            >
              Annuler
            </Link>
          </div>
        </form>
      </AdminCard>

      {/* Liens bas de page */}
      <div style={{
        marginTop: 24, paddingTop: 16, borderTop: `1px solid ${border}`,
        display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <Link
          to="/admin/aides/catalogue"
          style={{ color: mutedColor, fontSize: 14, textDecoration: 'none' }}
        >
          ← Retour au catalogue des aides
        </Link>
        {!isNew && (
          <Link
            to={`/admin/aides/catalogue/${id}/history`}
            style={{ color: '#D4724A', fontSize: 14, textDecoration: 'none', fontWeight: 500 }}
          >
            Voir l'historique des modifications →
          </Link>
        )}
      </div>
    </div>
  );
}
